import { Maniiifest, ManiiifestAnnotationPage } from 'maniiifest';
import { fetchResource } from './resource.ts';
import { createError } from '../errors/structured.ts';
import { IIIFAnnotation } from '../types/index.ts';
import { logger } from '../utils/logger.ts';
import { maxAnnotationPages } from '../config/appConfig.ts';
import { toGeoJsonBodyFeature } from './geojson.ts';
import { GeoJsonBodyFeature } from '../types/index.ts';

const manifestCache: Record<string, Maniiifest> = {}; // In-memory cache


async function processAnnotationsWorker(
  manifest: ManiiifestAnnotationPage,
  targetUrl: string,
): Promise<IIIFAnnotation[]> {
  const resultsMap: Map<string, IIIFAnnotation> = new Map();

  for (const annotation of manifest.iterateAnnotationPageAnnotation()) {
    const id = annotation.id;
    const rawMotivation = annotation.motivation;
    const motivation = Array.isArray(rawMotivation) ? rawMotivation[0] : rawMotivation || '';

    // Georeferencing annotations define the canvas geo->pixel transform (their
    // FeatureCollection body holds Ground Control Points); they are consumed by
    // `extractCanvasGcps`, not shown as content annotations.
    if (motivation === 'georeferencing') continue;

    const annotationParser = Maniiifest.parseAnnotation(annotation);

    const body = Array.from(annotationParser.iterateAnnotationTextualBody()).map((bodyItem: any) => ({
      ...bodyItem,
      language:
        typeof bodyItem.language === 'object' &&
          bodyItem.language !== null &&
          'value' in bodyItem.language
          ? bodyItem.language.value
          : bodyItem.language,
    }));

    // GeoJSON `Feature` / `FeatureCollection` bodies (e.g. IIIF Cookbook recipe
    // 0139) carry geographic geometry describing where the target region is
    // located on Earth. Collect them so the annotation list can render them on a
    // basemap; the geometry stays in geo space and is never drawn on the image.
    const rawBodyFeatures = Array.from(annotationParser.iterateAnnotationFeature());
    if (rawBodyFeatures.length === 0) {
      const singleBody = annotationParser.getAnnotationBody() as any;
      if (singleBody && singleBody.type === 'Feature') {
        rawBodyFeatures.push(singleBody);
      } else if (Array.isArray(singleBody)) {
        for (const item of singleBody) {
          if (item && item.type === 'Feature') rawBodyFeatures.push(item);
        }
      }
    }
    const geo = rawBodyFeatures
      .map(toGeoJsonBodyFeature)
      .filter((f): f is GeoJsonBodyFeature => f !== null);

    for (const target of annotationParser.iterateAnnotationTarget()) {
      const normalizedTargets = normalizeAnnotationTargets(target);

      for (const targetId of normalizedTargets) {
        const cleanTargetId = targetId.split('#')[0];
        if (cleanTargetId !== targetUrl) continue;

        if (!resultsMap.has(id)) {
          resultsMap.set(id, {
            id,
            motivation,
            target: [],
            generator: (annotation as any).generator,
            body,
            ...(geo.length > 0 ? { geo } : {}),
          });
        }

        resultsMap.get(id)!.target.push(targetId);
      }
    }
  }

  return Array.from(resultsMap.values());
}


async function processAnnotations(
  rawPage: any,
  targetUrl: string,
  signal?: AbortSignal,
): Promise<IIIFAnnotation[]> {
  let currentPage: any = rawPage;
  let allAnnotations: IIIFAnnotation[] = [];
  const visited = new Set<string>();
  let pageCount = 0;

  while (currentPage) {
    if (signal?.aborted) throw new DOMException('Aborted', 'AbortError');

    const parser = Maniiifest.parseAnnotationPage(currentPage);
    const result = await processAnnotationsWorker(parser, targetUrl);
    allAnnotations = allAnnotations.concat(result);

    const nextPage = (parser.getAnnotationPage()?.next ?? currentPage?.next) as any;
    const nextPageUrl = typeof nextPage === 'string' ? nextPage : nextPage?.id;
    pageCount += 1;
    if (nextPageUrl && pageCount >= maxAnnotationPages) {
      logger.warn(
        `Annotation pagination cap reached (${maxAnnotationPages}); stopping at ${nextPageUrl}`,
      );
      break;
    }
    if (nextPageUrl && visited.has(nextPageUrl)) {
      logger.warn(`Annotation pagination cycle detected at ${nextPageUrl}`);
      break;
    }
    if (nextPageUrl) {
      visited.add(nextPageUrl);
      const resource = await fetchResource(nextPageUrl, { signal });
      if (!resource.type || resource.type !== 'AnnotationPage') {
        throw createError('NETWORK_ANNOTATION_FETCH', 'No JSON data returned from fetchJson', { cause: resource });
      }
      currentPage = resource.data;
    } else {
      currentPage = null;
    }
  }

  return allAnnotations;
}



async function processAnnotationPageRef(
  annotationPageUrl: string,
  targetUrl: string,
  signal?: AbortSignal,
): Promise<IIIFAnnotation[]> {
  const resource = await fetchResource(annotationPageUrl, { signal });
  if (!resource.type || resource.type !== 'AnnotationPage') {
    throw createError('NETWORK_ANNOTATION_FETCH', 'No JSON data returned from fetchJson', { cause: resource });
  }
  return processAnnotations(resource.data, targetUrl, signal);
}

async function processAnnotationPage(
  page: any,
  targetUrl: string,
  signal?: AbortSignal,
): Promise<IIIFAnnotation[]> {
  return processAnnotations(page, targetUrl, signal);
}

export async function getAnnotationsForTarget(
  manifestUrl: string,
  targetUrl: string,
  signal?: AbortSignal,
): Promise<IIIFAnnotation[]> {
  let parser: Maniiifest;

  if (manifestCache[manifestUrl]) {
    logger.debug(`Using cached manifest for: ${manifestUrl}`);
    parser = manifestCache[manifestUrl];
  } else {
    logger.info(`Fetching new manifest from: ${manifestUrl}`);
  const resource = await fetchResource(manifestUrl, { signal });
    if (!resource.type || resource.type !== 'Manifest') {
      throw createError('NETWORK_MANIFEST_FETCH', 'No JSON data returned from fetchJson', { cause: resource });
    }
    parser = new Maniiifest(resource.data);
    manifestCache[manifestUrl] = parser;
  }

  const type = parser.getSpecificationType();

  if (type !== 'Manifest') {
    throw new Error('Specification should be a Manifest');
  }

  let allAnnotations: IIIFAnnotation[] = [];

  for (const canvas of parser.iterateManifestCanvas()) {
    if (canvas.id === targetUrl) {
      logger.debug(`Processing canvas: ${canvas.id}`);

      const annotations = canvas.annotations;

      if (Array.isArray(annotations) && annotations.length > 0) {
        for (const annotationPage of annotations) {
          let result: IIIFAnnotation[] = [];
          if (Array.isArray(annotationPage.items)) {
            result = await processAnnotationPage(annotationPage, targetUrl, signal);
          } else if (typeof annotationPage.id === 'string') {
            result = await processAnnotationPageRef(
              annotationPage.id,
              targetUrl,
              signal,
            );
          } else {
            logger.warn(
              `Invalid annotation format in canvas ${canvas.id}:`,
              annotationPage,
            );
          }
          allAnnotations = allAnnotations.concat(result);
        }
      } else {
        logger.warn(`Canvas ${canvas.id} has no valid annotations.`);
      }

      return allAnnotations;
    }
  }

  logger.warn(`No matching canvas found for target: ${targetUrl}`);
  return [];
}

export function normalizeAnnotationTargets(target: any): string[] {
  const results: string[] = [];

  const flattenTarget = (t: any): void => {
    if (typeof t === 'string') {
      results.push(t);
    } else if (Array.isArray(t)) {
      t.forEach(flattenTarget);
    } else if (t && typeof t === 'object') {
      // Priority 1: use `id` if present
      if (typeof t.id === 'string') {
        results.push(t.id);
        return;
      }

      // Priority 2: handle `source` + optional `selector`
      const source = typeof t.source === 'string'
        ? t.source
        : typeof t.source?.id === 'string'
          ? t.source.id
          : null;

      if (source) {
        const selectorStr = extractSelector(t.selector);
        if (selectorStr) {
          results.push(`${source}#${selectorStr}`);
        } else {
          results.push(source);
        }
        return;
      }

      // Fallback: if none of the above work, check `value`
      if (typeof t.value === 'string') {
        results.push(t.value);
      }
    }
  };

  flattenTarget(target);
  return results;
}


function extractSelector(selector: any): string | null {
  if (!selector) return null;

  if (typeof selector === 'string') {
    return selector.replace(/^#/, '');
  }

  if (Array.isArray(selector)) {
    for (const sel of selector) {
      const extracted = extractSelector(sel);
      if (extracted) return extracted;
    }
    return null;
  }

  if (typeof selector === 'object') {
    if (selector.type === 'SvgSelector' && typeof selector.value === 'string') {
      return `svg=${encodeURIComponent(selector.value)}`;
    }

    if (typeof selector.value === 'string') {
      return selector.value.replace(/^#/, '');
    }

    if (selector.type === 'FragmentSelector' && typeof selector.value === 'string') {
      return selector.value;
    }

    if (selector.type === 'TextQuoteSelector') {
      const exact = selector.exact || '';
      const prefix = selector.prefix || '';
      const suffix = selector.suffix || '';
      return `text=${encodeURIComponent(prefix)}${encodeURIComponent(exact)}${encodeURIComponent(suffix)}`;
    }

    if (selector.region) {
      return `xywh=${selector.region}`;
    }
  }

  return null;
}

